import type { CampaignPricingSku } from '$lib/data/campaign/pricingCampaignSkus';
import { encodeCampaignSku, findCampaignSku } from '$lib/data/campaign/pricingCampaignSkus';

const ALLOWED_FROM = new Set([
  '/campaign/pricing',
  '/campaign/pricing-1',
  '/pricing/feature-film',
  '/pricing/television',
  '/pricing/theater',
  '/pricing/additional-offerings',
]);

/* eslint-disable no-unused-vars -- parameter names document injected dependency contracts */
type CheckoutDependencies = {
  authenticated: boolean;
  campaignPath: string;
  navigate: (url: string) => void | Promise<void>;
};
/* eslint-enable no-unused-vars */

export function formatCampaignUsd(amountCents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);
}

export function formatCampaignUsdParts(amountCents: number): { dollars: string; cents: string } {
  const [dollars, cents] = formatCampaignUsd(amountCents).replace(/^\$/, '').split('.');
  return { dollars, cents: cents ?? '00' };
}

export function campaignCheckoutFromPath(raw: string | null | undefined): string {
  if (raw && ALLOWED_FROM.has(raw)) return raw;
  return '/campaign/pricing';
}

export function campaignCheckoutHref(sku: CampaignPricingSku, campaignPath: string): string {
  const params = new URLSearchParams({
    buy: encodeCampaignSku(sku),
    from: campaignCheckoutFromPath(campaignPath),
  });
  return `/campaign/checkout?${params.toString()}`;
}

export function catalogCheckoutHref(
  productType: string | undefined,
  analysesCount: number | undefined,
  fromPath: string,
): string | null {
  if (!productType || !analysesCount) return null;
  const sku = findCampaignSku(`${productType}:${analysesCount}`);
  if (!sku) return null;
  return campaignCheckoutHref(sku, fromPath);
}

export function campaignCheckoutRegisterHref(checkoutHref: string): string {
  const loginParams = new URLSearchParams({
    mode: 'register',
    returnUrl: checkoutHref,
  });
  return `/login?${loginParams.toString()}`;
}

export function resolveCampaignCheckoutQuery(
  buy: string | null,
  from: string | null,
):
  | { sku: CampaignPricingSku; fromPath: string; redirectTo: null }
  | { sku: undefined; fromPath: string; redirectTo: string } {
  const fromPath = campaignCheckoutFromPath(from);
  const sku = findCampaignSku(buy);
  if (!sku) {
    return { sku: undefined, fromPath, redirectTo: fromPath };
  }
  return { sku, fromPath, redirectTo: null };
}

export async function startCampaignCheckout(
  sku: CampaignPricingSku,
  dependencies: CheckoutDependencies
): Promise<void> {
  const { authenticated, campaignPath, navigate } = dependencies;
  const checkoutHref = campaignCheckoutHref(sku, campaignPath);

  if (!authenticated) {
    const loginParams = new URLSearchParams({
      mode: 'register',
      returnUrl: checkoutHref,
    });
    await navigate(`/login?${loginParams.toString()}`);
    return;
  }

  await navigate(checkoutHref);
}
